<template>
  <div class="h-14 sm:h-20">
    <div class="relative w-full h-full" :class="className" ref="containerRef">
      <div class="flex overflow-hidden absolute inset-0 items-center rounded-lg bg-black/90">
        <!-- Video Frames -->
        <div class="flex absolute inset-0 h-full">
          <div v-for="(frame, index) in imgList" :key="index" class="relative h-full"
            :style="{ width: `${100 / imgList.length}%` }">
            <img :src="frame.img" :alt="`Frame ${index}`" class="object-cover w-full h-full" />
          </div>
        </div>


        <div class="absolute top-0 bottom-0 z-10 border-y-2  rounded-lg border-[#F06906] cursor-move" :style="{
          left: `${(store.startTime / store.duration) * 100}%`,
          width: `${((store.endTime - store.startTime) / store.duration) * 100}%`
        }" @mousedown="(e) => onSelectionDrag(e)" @touchstart="(e) => onSelectionTouchStart(e)">
        </div>

        <!-- Timeline Controls -->
        <div class="absolute inset-0">
          <!-- Selection Frame -->


          <!-- Start Handle Hide -->
          <div class="absolute top-0 bottom-0 left-0 bg-black/50"
            :style="{ width: `${(store.startTime / store.duration) * 100}%` }" />

          <!-- End Handle Hide -->
          <div class="absolute top-0 right-0 bottom-0 bg-black/50"
            :style="{ width: `${((store.duration - store.endTime) / store.duration) * 100}%` }" />

          <!-- Start Handle -->
          <div class="absolute top-0 bottom-0 w-3 z-50 bg-[#F06906] cursor-ew-resize rounded-l-lg  overflow-hidden"
            :style="{
              left: `${(store.startTime / store.duration) * 100}%`,
              transform: 'none'
            }" @mousedown="(e) => onHandleDrag(e, true)" @touchstart="(e) => onTouchStart(e, true)">
            <div class="absolute left-1 top-1/2 -mt-1.5 w-[4px] h-3 bg-black/25 rounded-sm" />
          </div>

          <!-- End Handle -->
          <div class="absolute top-0 bottom-0 w-3 z-50 bg-[#F06906] cursor-ew-resize rounded-r-lg  overflow-hidden"
            :style="{
              left: `${(store.endTime / store.duration) * 100}%`,
              transform: 'translateX(-100%)'
            }" @mousedown="(e) => onHandleDrag(e, false)" @touchstart="(e) => onTouchStart(e, false)">
            <div class="absolute left-1 top-1/2 -mt-1.5 w-[4px] h-3 bg-black/25 rounded-sm" />
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useVideoEditorStore } from './store';
import { formatTime } from './utils';

interface Props {
  className?: string
  minDistance?: number
  frames: Array<{ ts: number; img: Blob }>
}

const props = withDefaults(defineProps<Props>(), {
  className: '',
  minDistance: 1000 * 1000 * 60 * 0.5,
  frames: () => [],
})

const store = useVideoEditorStore()
const previousTime = ref(0)
const containerRef = ref<HTMLElement | null>(null)
const imgList = ref<Array<{ ts: number; img: string }>>([])
const isDragging = ref(false)
const dragTarget = ref<'start' | 'end' | null>(null)


// 跟踪选择区域的拖拽
const selectionDragStart = ref<{ x: number; startTime: number; endTime: number } | null>(null)

// 监听当前时间的变化
watch(() => store.currentTime, (newTime, oldTime) => {
  previousTime.value = oldTime
})

watch(() => store.videoFrames, (newFrames) => {
  // Clean up existing object URLs
  imgList.value = newFrames.map(frame => ({
    ts: frame.ts,
    img: URL.createObjectURL(frame.img)
  }))
})

function getTimeFromPosition(position: number): number {
  if (!containerRef.value) return 0
  const width = containerRef.value.clientWidth
  return (position / width) * store.duration
}

// 处理拖拽
function onHandleDrag(e: MouseEvent, isStartHandle: boolean) {
  e.preventDefault()
  isDragging.value = true
  dragTarget.value = isStartHandle ? 'start' : 'end'
}

// 处理鼠标移动
function handleMouseMove(e: MouseEvent) {
  if (!isDragging.value || !containerRef.value) return

  const rect = containerRef.value.getBoundingClientRect()
  const position = e.clientX - rect.left
  const newTime = getTimeFromPosition(position)

  if (dragTarget.value === 'start') {
    if (newTime < store.endTime - props.minDistance) {
      // 设置时间范围
      const startTime = Math.max(0, newTime)
      store.setTimeRange(startTime, store.endTime)
      // 设置当前时间
      // 更新当前播放时间
      store.updateVideoTime(startTime)
    }
  } else if (dragTarget.value === 'end') {
    if (newTime > store.startTime + props.minDistance) {
      // 设置时间范围
      const endTime = Math.min(store.duration, newTime)
      store.setTimeRange(store.startTime, endTime)
      // 设置当前时间
      store.updateVideoTime(endTime)
    }
  } else if (selectionDragStart.value) {
    // 处理选择区域的拖拽
    const delta = e.clientX - selectionDragStart.value.x
    const deltaTime = (delta / containerRef.value.clientWidth) * store.duration
    const newStartTime = Math.max(0, selectionDragStart.value.startTime + deltaTime)
    const newEndTime = Math.min(store.duration, selectionDragStart.value.endTime + deltaTime)

    // 确保不会超出边界
    if (newStartTime >= 0 && newEndTime <= store.duration
      // 确保裁剪时长大于最小裁剪时长
      && (newEndTime - newStartTime) >= props.minDistance) {
      store.setTimeRange(newStartTime, newEndTime)
      store.updateVideoTime(newStartTime)
    }
  }
}

// 处理鼠标抬起
function handleMouseUp() {
  isDragging.value = false
  dragTarget.value = null
}

// 处理触摸开始
function onTouchStart(e: TouchEvent, isStartHandle: boolean) {
  e.preventDefault()
  isDragging.value = true
  dragTarget.value = isStartHandle ? 'start' : 'end'
}

// 处理触摸移动
function handleTouchMove(e: TouchEvent) {
  if (!isDragging.value || !containerRef.value) return

  const touch = e.touches[0]
  const rect = containerRef.value.getBoundingClientRect()
  const position = touch.clientX - rect.left
  const newTime = getTimeFromPosition(position)

  // 如果拖拽的是开始时间
  if (dragTarget.value === 'start') {
    // 如果新的开始时间小于结束时间减去最小距离
    if (newTime < store.endTime - props.minDistance) {
      store.setTimeRange(Math.max(0, newTime), store.endTime)
      // 更新当前播放时间
      store.updateVideoTime(newTime)
    }
  } else if (dragTarget.value === 'end') {
    // 如果新的结束时间大于开始时间加上最小距离
    if (newTime > store.startTime + props.minDistance) {
      store.setTimeRange(store.startTime, Math.min(store.duration, newTime))
      // 更新当前播放时间
      store.updateVideoTime(newTime)
    }
  } else if (selectionDragStart.value) {
    // 处理选择区域的拖拽
    const delta = e.touches[0].clientX - selectionDragStart.value.x
    const deltaTime = (delta / containerRef.value.clientWidth) * store.duration
    const newStartTime = Math.max(0, selectionDragStart.value.startTime + deltaTime)
    const newEndTime = Math.min(store.duration, selectionDragStart.value.endTime + deltaTime)

    // 确保不会超出边界
    if (
      // 确保开始时间大于等于0
      newStartTime >= 0 && newEndTime <= store.duration
      // 确保裁剪时长大于最小裁剪时长
      && (newEndTime - newStartTime) >= props.minDistance) {
      store.setTimeRange(newStartTime, newEndTime)
      // 更新当前播放时间
      store.updateVideoTime(newStartTime)
    }
  }
}

function handleTouchEnd() {
  isDragging.value = false
  dragTarget.value = null
}

// 添加选择区域拖拽处理函数
function onSelectionDrag(e: MouseEvent) {
  e.preventDefault()
  isDragging.value = true
  dragTarget.value = null
  selectionDragStart.value = {
    x: e.clientX,
    startTime: store.startTime,
    endTime: store.endTime
  }
}

function onSelectionTouchStart(e: TouchEvent) {
  e.preventDefault()
  isDragging.value = true
  dragTarget.value = null
  selectionDragStart.value = {
    x: e.touches[0].clientX,
    startTime: store.startTime,
    endTime: store.endTime
  }
}

// MP4Clip initialization

onMounted(() => {
  // Add both mouse and touch event listeners
  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
  document.addEventListener('touchmove', handleTouchMove, { passive: false })
  document.addEventListener('touchend', handleTouchEnd)
  document.addEventListener('touchcancel', handleTouchEnd)
})

onUnmounted(() => {
  // Cleanup
  imgList.value.forEach(frame => {
    URL.revokeObjectURL(frame.img)
  })
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
  document.removeEventListener('touchmove', handleTouchMove)
  document.removeEventListener('touchend', handleTouchEnd)
  document.removeEventListener('touchcancel', handleTouchEnd)
})

// 计算剩余播放时间
const remainingTime = computed(() => {
  if (!store.isPlaying) return 0
  return (store.endTime - store.currentTime) / 1000000 // 转换为秒
})
</script>

<style scoped>
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 56px;
  color: rgba(255, 255, 255, 0.5);
}

.no-scroll {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.no-scroll::-webkit-scrollbar {
  display: none;
}

.timeline-indicator {
  animation: moveIndicator 250ms linear;
}

@keyframes moveIndicator {
  from {
    left: var(--previous-left);
  }

  to {
    left: var(--current-left);
  }
}

/* .timeline-current-indicator {
  position: absolute;
  background-color: #fff;
  bottom: -15px;
  top: -15px;
  width: 1px;
  opacity: .75;
  font-size: 0;
  z-index: 10;
  will-change: transform;
}

.timeline-current-indicator::before {
  content: "";
  position: absolute;
  top: 0;
  left: -4px;
  width: 9px;
  height: 7px;
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23ffffff' width='9' height='7' viewBox='0 0 9 7'%3E%3Cpath d='M9,0L4.5,7L0,0H9z'/%3E%3C/svg%3E");
  background-position: 50%;
  background-repeat: no-repeat;
  display: block;
} */

*,
*::before,
*::after {
  box-sizing: border-box;
}
</style>